import React, { useEffect, useState } from 'react';
import { LogoUNEA } from '../../components/common/Logo';
import MIcon from '../../components/common/MIcon';
import { api } from '../../services/api';
import { Button } from '../../components/ui';
import { FloatInput, FloatSelect, FloatTextarea } from '../../components/common/FloatField';
import WatcherEmailsField from '../../components/common/WatcherEmailsField';
import { useFrontDeskAuth } from '../../context/FrontDeskAuthContext';
import FrontDeskLogin from './FrontDeskLogin';
import PortalNotice from '../../components/common/PortalNotice';
import TrackForm from '../../components/common/TrackForm';
import { useT } from '../../context/TenantContext';
import './frontdesk.css';

const CATEGORY_LABELS = {
  laptop:    'Portátil',
  projector: 'Proyector',
  tablet:    'Tablet',
  cable:     'Cable',
  other:     'Otro',
};

export default function LoanRequest() {
  const { t } = useT();
  const { fdUser, canAccess, fdLoading, authError } = useFrontDeskAuth();

  const [success,   setSuccess]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors,    setErrors]    = useState({});

  const today = new Date().toISOString().split('T')[0];

  const [form, setForm] = useState({
    requested_category: '',
    requester_name:  '',
    requester_email: '',
    watcher_emails:  '',
    purpose:         '',
    loan_date:       '',
    expected_return: '',
    notes:           '',
  });

  useEffect(() => {
    if (fdUser) {
      setForm(f => ({ ...f, requester_name: fdUser.name || '', requester_email: fdUser.email || '' }));
    }
  }, [fdUser?.email]);

  const set = k => e => {
    setForm(f => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors(ev => ({ ...ev, [k]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.requested_category)         errs.requested_category = 'Indica qué tipo de equipo necesitas';
    if (!fdUser && !form.requester_name.trim())  errs.requester_name  = 'El nombre es obligatorio';
    if (!fdUser && !form.requester_email.trim()) errs.requester_email = 'El email es obligatorio';
    if (!form.purpose.trim())             errs.purpose     = 'El propósito es obligatorio';
    if (!form.loan_date)                  errs.loan_date        = 'La fecha de préstamo es obligatoria';
    if (!form.expected_return)            errs.expected_return  = 'La fecha de devolución es obligatoria';
    if (form.loan_date && form.expected_return && form.expected_return < form.loan_date) {
      errs.expected_return = 'La fecha de devolución debe ser posterior al préstamo';
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      await api.createLoanRequest({
        requested_category: form.requested_category,
        requester_name:  form.requester_name,
        requester_email: form.requester_email,
        watcher_emails:  form.watcher_emails.split(',').map(x => x.trim()).filter(Boolean),
        purpose:         form.purpose,
        loan_date:       form.loan_date || null,
        expected_return: form.expected_return || null,
        notes:           form.notes || null,
      });
      setSuccess(true);
    } catch(e) {
      setErrors({ submit: e.message || 'Error al enviar la solicitud' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleNew = () => {
    setSuccess(false);
    setForm(f => ({ requested_category:'', requester_name: fdUser?.name || '', requester_email: fdUser?.email || '', watcher_emails:'', purpose:'', loan_date:'', expected_return:'', notes:'' }));
    setErrors({});
  };

  if (fdLoading) return null;
  if (!canAccess) return <FrontDeskLogin error={authError} />;

  return (
    <div style={{ minHeight:'100vh', background:'var(--fd-bg)' }}>
      <header className="fd-appbar">
        <div style={{display:'flex',alignItems:'center',gap:0}}>
          <LogoUNEA height={32} style={{display:'block'}} />
          <div style={{width:1,height:28,background:'var(--fd-border)',margin:'0 16px'}}/>
          <span style={{fontSize:15,fontWeight:400,color:'var(--fd-text)'}}>HelpDesk TI</span>
        </div>
      </header>

      <PortalNotice />

      <div className="fd-two-col">

        {/* ── COLUMNA IZQUIERDA — igual que en el resto del portal ── */}
        <div className="fd-col-left">
          <div className="fd-card fd-card-side">
            <h1 className="fd-side-title">
              Préstamo de<br/>
              <em style={{fontStyle:'normal',color:'var(--fd-accent)'}}>material</em>
            </h1>
            <p style={{fontSize:13,color:'var(--fd-muted)',lineHeight:1.7,margin:0}}>
              Pide prestado un portátil, proyector u otro equipo por un tiempo determinado.
            </p>

            <div className="fd-side-section" style={{borderTop:'none',paddingTop:0}}>
              <div style={{height:1,background:'var(--fd-border)',margin:'20px 0'}}/>
              <div className="fd-side-section-title">{t('fd.have_ticket')}</div>
              <TrackForm />
            </div>
          </div>
        </div>

        {/* ── CARD DERECHO ANCHO — formulario ── */}
        <div className="fd-col-right fd-card fd-card-main">
          <div className="fd-form-header">
            <h2 style={{fontSize:16,fontWeight:600,color:'var(--fd-text)',marginBottom:3}}>
              Solicitud de préstamo
            </h2>
          </div>

          {success ? (
            <div style={{padding:'48px 32px',textAlign:'center'}}>
              <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
              <h2 style={{ fontSize:20, fontWeight:700, color:'#15803D', margin:'0 0 12px' }}>
                Solicitud enviada correctamente
              </h2>
              <p style={{ fontSize:14, color:'var(--fd-muted)', maxWidth:440, margin:'0 auto 24px', lineHeight:1.6 }}>
                El equipo de TI revisará tu solicitud y te contactará por email.
              </p>
              <Button variant="success" onClick={handleNew}>Nueva solicitud</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Datos personales */}
              <div style={{padding:'20px 24px 0'}}>
                <div className="fd-section-title">{t('fd.your_data')}</div>

                {fdUser ? (
                  <div style={{fontSize:13,color:'var(--fd-muted)',padding:'10px 14px',background:'var(--fd-bg)',borderRadius:'var(--radius-sm)',display:'flex',alignItems:'center',gap:8,marginBottom:16}}>
                    <MIcon name="check_circle" size={16} style={{color:'#15803D'}}/>
                    Se registrará a tu nombre: <strong style={{color:'var(--fd-text)'}}>{fdUser.name}</strong> ({fdUser.email})
                  </div>
                ) : (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', columnGap:16, rowGap:16, marginBottom:16 }}>
                    <FloatInput label="Nombre completo" required id="requester_name"
                      value={form.requester_name} onChange={set('requester_name')} autoComplete="name" />
                    <FloatInput label="Email institucional" required id="requester_email" type="email"
                      value={form.requester_email} onChange={set('requester_email')} autoComplete="email" />
                    {(errors.requester_name || errors.requester_email) && (
                      <div style={{ fontSize:11, color:'#EF4444', gridColumn:'1 / -1' }}>
                        {errors.requester_name || errors.requester_email}
                      </div>
                    )}
                  </div>
                )}

                <div style={{marginBottom:16}}>
                  <WatcherEmailsField value={form.watcher_emails} onChange={v => setForm(f => ({...f, watcher_emails: v}))} />
                </div>
              </div>

              {/* Tipo de equipo necesitado — el técnico asigna la unidad concreta al aprobar */}
              <div style={{padding:'20px 24px 0'}}>
                <FloatSelect label="¿Qué tipo de equipo necesitas?" required id="requested_category"
                  value={form.requested_category} onChange={set('requested_category')}>
                  <option value=""></option>
                  {Object.entries(CATEGORY_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                </FloatSelect>
                {errors.requested_category && <div style={{ fontSize:12, color:'#B91C1C', marginTop:6 }}>⚠ {errors.requested_category}</div>}
              </div>

              {/* Descripción */}
              <div style={{padding:'20px 24px 0'}}>
                <FloatTextarea label="Propósito / Motivo del préstamo" required id="purpose" rows={3}
                  value={form.purpose} onChange={set('purpose')} />
                {errors.purpose && <div style={{ fontSize:11, color:'#EF4444', marginTop:4 }}>{errors.purpose}</div>}
              </div>

              <div style={{padding:'20px 24px 0', display:'grid', gridTemplateColumns:'1fr 1fr', columnGap:16, rowGap:16}}>
                <FloatInput label="Fecha de préstamo" required id="loan_date" type="date"
                  min={today} value={form.loan_date} onChange={set('loan_date')} />
                <FloatInput label="Fecha de devolución prevista" required id="expected_return" type="date"
                  min={form.loan_date || today} value={form.expected_return} onChange={set('expected_return')} />
                {(errors.loan_date || errors.expected_return) && (
                  <div style={{ fontSize:11, color:'#EF4444', gridColumn:'1 / -1' }}>{errors.loan_date || errors.expected_return}</div>
                )}
              </div>

              <div style={{padding:'16px 24px 0'}}>
                <FloatTextarea label="Notas adicionales (opcional)" id="notes" rows={2}
                  value={form.notes} onChange={set('notes')} />
              </div>

              {errors.submit && (
                <div style={{margin:'16px 24px 0', fontSize:13, color:'#B91C1C', background:'#FEF2F2', borderRadius:'var(--radius-sm)', padding:'10px 16px'}}>
                  ⚠ {errors.submit}
                </div>
              )}

              <div style={{padding:'20px 24px 24px', display:'flex', justifyContent:'flex-end'}}>
                <Button variant="primary" type="submit" disabled={submitting}>
                  {submitting ? 'Enviando…' : 'Enviar solicitud'}
                </Button>
              </div>
            </form>
          )}
        </div>

      </div>
    </div>
  );
}
